/**
 * Shared flipbook UI state. Lives above both surfaces that drive the
 * flipbook — the bottom filmstrip (FlipbookBar) and the Animation panel's
 * Flipbook mode — so they are the same flipbook rather than two copies that
 * drift apart.
 *
 * Scene-backed values (pages, per-page duration) are NOT here; they are read
 * from the scene. Only ephemeral UI state lives in this context.
 */
import React, { createContext, useContext, useMemo, useState } from "react";

export type PlayMode = "loop" | "pingpong" | "once";

export type OnionSettings = {
  enabled: boolean;
  opacity: number;
  /** ghost frames before/after the active one, 0–3 each */
  before: number;
  after: number;
  tint: boolean;
};

export const DEFAULT_FPS = 8;

export const DEFAULT_ONION: OnionSettings = {
  enabled: true,
  opacity: 0.35,
  before: 1,
  after: 1,
  tint: false,
};

type FlipbookState = {
  open: boolean;
  setOpen: (v: boolean) => void;
  /** true while the Animation panel's Flipbook mode is mounted, so the always-
   * mounted FlipbookBar's onion effect renders even with the bottom bar closed */
  flipbookActive: boolean;
  setFlipbookActive: (v: boolean) => void;
  activeId: string | null;
  setActiveId: (v: string | null) => void;
  /** id of the book being edited; null = "whichever book is first in the
   * scene", resolved against the scene by the consumers (a stored id would go
   * stale the moment that book's last page is deleted) */
  activeBookId: string | null;
  setActiveBookId: (v: string | null) => void;
  playing: boolean;
  setPlaying: (v: boolean) => void;
  playMode: PlayMode;
  setPlayMode: (v: PlayMode) => void;
  onion: OnionSettings;
  setOnion: React.Dispatch<React.SetStateAction<OnionSettings>>;
  fps: number;
  setFps: (v: number) => void;
  /** id of the chosen export target (see animation/export/targets.ts). Lives
   * here, not in SettingsPanel, because the Flipbook panel's "Export" button
   * preselects a book before handing over to the export panel — one source of
   * truth beats an event + a mirrored local state. null = first target. */
  exportTargetId: string | null;
  setExportTargetId: (v: string | null) => void;
};

const FlipbookContext = createContext<FlipbookState | null>(null);

export const FlipbookProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const [flipbookActive, setFlipbookActive] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeBookId, setActiveBookId] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [playMode, setPlayMode] = useState<PlayMode>("loop");
  const [onion, setOnion] = useState<OnionSettings>(DEFAULT_ONION);
  const [fps, setFps] = useState(DEFAULT_FPS);
  const [exportTargetId, setExportTargetId] = useState<string | null>(null);

  const value = useMemo(
    () => ({
      open,
      setOpen,
      flipbookActive,
      setFlipbookActive,
      activeId,
      setActiveId,
      activeBookId,
      setActiveBookId,
      playing,
      setPlaying,
      playMode,
      setPlayMode,
      onion,
      setOnion,
      fps,
      setFps,
      exportTargetId,
      setExportTargetId,
    }),
    [
      open,
      flipbookActive,
      activeId,
      activeBookId,
      playing,
      playMode,
      onion,
      fps,
      exportTargetId,
    ],
  );

  return (
    <FlipbookContext.Provider value={value}>
      {children}
    </FlipbookContext.Provider>
  );
};

export const useFlipbook = (): FlipbookState => {
  const ctx = useContext(FlipbookContext);
  if (!ctx) {
    throw new Error("useFlipbook must be used inside <FlipbookProvider>");
  }
  return ctx;
};
